import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { api, euro } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ReviewDialog } from "@/components/ReviewDialog";
import { Loader2, Calendar, MapPin, Users, Clock, Star } from "lucide-react";

const STATUS = {
  pending: { label: "In Bearbeitung", cls: "bg-amber-100 text-amber-700" },
  confirmed: { label: "Bestätigt", cls: "bg-emerald-100 text-emerald-700" },
  declined: { label: "Abgelehnt", cls: "bg-red-100 text-red-700" },
  completed: { label: "Abgeschlossen", cls: "bg-slate-200 text-slate-700" },
};
const PAY = { pending: "Offen", paid: "Bezahlt", failed: "Fehlgeschlagen" };

export default function CustomerDashboard() {
  const [bookings, setBookings] = useState(null);
  const [reviewBooking, setReviewBooking] = useState(null);
  const load = () => api.get("/bookings/mine").then((r) => setBookings(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10">
        <h1 className="font-heading text-3xl font-extrabold text-slate-900 mb-1">Meine Buchungen</h1>
        <p className="text-slate-500 mb-8">Übersicht deiner angefragten und bestätigten Events</p>
        {bookings === null ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-20 text-slate-500" data-testid="no-bookings">Noch keine Buchungen. Entdecke jetzt Angebote!</div>
        ) : (
          <div className="space-y-4" data-testid="customer-bookings">
            {bookings.map((b) => (
              <div key={b.id} data-testid={`booking-${b.id}`} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col sm:flex-row">
                <img src={b.service_image} alt="" className="w-full sm:w-40 h-40 sm:h-auto object-cover" />
                <div className="p-5 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xs text-slate-400 font-mono">{b.reference}</div>
                      <h3 className="font-heading font-bold text-lg text-slate-900">{b.service_title}</h3>
                      <p className="text-sm text-slate-500">{b.partner_name} · {b.event_type}</p>
                    </div>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS[b.booking_status].cls}`}>{STATUS[b.booking_status].label}</span>
                  </div>
                  <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-600">
                    <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {b.event_date} {b.event_time}</span>
                    <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {b.guests}</span>
                    <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {b.hours}h</span>
                    <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {b.city}</span>
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                    <span className="text-xs text-slate-500">{b.payment_method === "stripe" ? "Kartenzahlung" : "Barzahlung am Event"} · {PAY[b.payment_status]}</span>
                    <span className="font-mono font-extrabold text-lg text-emerald-700">{euro(b.total_amount)}</span>
                  </div>
                  {["confirmed", "completed"].includes(b.booking_status) && (
                    b.reviewed
                      ? <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-semibold mt-3"><Star className="w-3.5 h-3.5 fill-emerald-600" /> Bewertet</span>
                      : <Button size="sm" variant="outline" className="mt-3 gap-1" onClick={() => setReviewBooking(b)} data-testid={`review-${b.id}`}><Star className="w-3.5 h-3.5" /> Event bewerten</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <ReviewDialog booking={reviewBooking} onOpenChange={() => setReviewBooking(null)} onSubmitted={load} />
    </div>
  );
}
